import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X, CreditCard, Scan, Check } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useProfileStore, useUserStore } from '../../stores'
import CardScanner from '../../components/CardScanner'
import { processCardFile, persistCardData } from '../../utils/processCardImage'
import { cardUploadFailureMessage } from '../../utils/uploadRefusal'
import DobPicker from '../../components/DobPicker'
// TEST 2.25 (P5 step 16): the server refuses a minor or invalid DOB on this profile with
// dob_under_18 / dob_invalid; dobRefusalKey turns either into the translated sentence.
import { dobRefusalKey, ACCOUNT_HOLDER_MIN_AGE } from '../../utils/age'
// TF-05 / UX-1 (v242.61.0): the save gate and the profile headline must ask the
// same question. They did not: this form demanded eight fields while the headline
// counted three, so a patient could read 100% and still be unable to save.
import { missingIdentificationFields, isIdentificationComplete } from '../../utils/profileCompletion'
import useCardScanPair from '../../hooks/useCardScanPair'
// TEST 1.02 / PROD-176 — the ONE rule for what a scan may write (shared with onboarding) and
// the ONE test for a refused card. See utils/idScanAutofill.js.
import { idScanFill, isIdScanRefused } from '../../utils/idScanAutofill'
import useResolvedCardUrls from '../../hooks/useResolvedCardUrls'
import CardThumb from '../../components/CardThumb' // s110 b77
import { cardInlineSrc } from '../../utils/cardRef'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'

export default function ProfileIdentification() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile, syncSection, loadProfile, syncing } = useProfileStore()
  const user = useUserStore((state) => state.user)
  
  const [form, setForm] = useState({
    firstName: '', lastName: '', dob: '', sex: '',
    streetAddress: '', apt: '', city: '', state: '', zipCode: '',
    idFront: null, idBack: null,
  })
  const [saving, setSaving] = useState(false)
  // PROD-237 (P17 step 8): card photos still uploading. Save waits for them, because a save
  //   during an upload now keeps the stored card instead of writing the unsaved one.
  const [cardUploads, setCardUploads] = useState(0)
  const [scannerOpen, setScannerOpen] = useState(null)
  // TEST 1.02 R2 — this session's answer from the server: the ID just scanned was refused,
  // or a front scanned since has been ACCEPTED (which supersedes a refusal stored earlier).
  const [idRefused, setIdRefused] = useState(false)
  const [idAcceptedNow, setIdAcceptedNow] = useState(false)

  useEffect(() => { loadProfile() }, [])

  useEffect(() => {
    if (profile?.identification) {
      // TEST 3.06: Sex is stored lowercase (utils/formatSex.js; onboarding and the ID scan
      // write 'male' / 'female' / 'other'); a value saved through the old capitalised
      // select ('Male') is lowercased here so it still matches an option.
      // PROD-1167: the stored phone / mobilePhone are NOT taken into the form: the number on this
      //   screen is the account's (users.phone, read-only below) and a save never carries one.
      const { phone: _p, mobilePhone: _m, ...identification } = profile.identification
      setForm(prev => ({ ...prev, ...identification, sex: (profile.identification.sex || '').toLowerCase() }))
    } else if (profile) {
      setForm(prev => ({ ...prev, firstName: profile.firstName || '', lastName: profile.lastName || '' }))
    }
  }, [profile, user])

  const handleSave = async () => {
    if (cardUploads > 0) return // P17 step 8: a card photo is still uploading
    // TF-05: the old message named nothing, so a patient whose onboarding never
    // asked for an address (it was gated behind uploading BOTH ID sides) saw only
    // "fill in all required fields" with no idea which. Name them.
    const missingFields = missingIdentificationFields(form)
    if (missingFields.length > 0) {
      const labels = { firstName: 'First name', lastName: 'Last name', dob: 'Date of birth',
                       sex: 'Sex', streetAddress: 'Street address', city: 'City',
                       state: 'State', zipCode: 'ZIP code' }
      alert('Please complete: ' + missingFields.map(f => labels[f] || f).join(', '))
      return
    }
    
    setSaving(true)
    try {
      const cleanForm = { ...form }
      if (cleanForm.idFront?.loading) delete cleanForm.idFront.loading
      if (cleanForm.idBack?.loading) delete cleanForm.idBack.loading
      
      const result = await syncSection('identification', { ...cleanForm, completed: true })
      setSaving(false)
      
      // ★ TEST 1.02 R2 — the SERVER refused a card on save (it enforces the rule whatever
      //   this screen did). Everything else was saved; stay here so the patient reads why.
      if (result.success && result.idDocumentRefused) {
        setIdRefused(true)
        setIdAcceptedNow(false)
        return
      }
      if (result.success) {
        navigate('/profile')
      } else if (dobRefusalKey(result)) {
        alert(t(dobRefusalKey(result), { age: ACCOUNT_HOLDER_MIN_AGE }))
      } else {
        const errorMsg = result.error?.includes('413') || result.error?.includes('too large')
          ? 'Data is too large. Please try again.' : (result.error || 'Unknown error')
        alert('Failed to save: ' + errorMsg)
      }
    } catch (err) {
      setSaving(false)
      console.error('Save error:', err)
      alert('Failed to save: ' + (err.message || 'Unknown error'))
    }
  }

  // PROD-1189: the front carries the OCR'd license fields and is the only side read; the
  // pair hook fires this as soon as a front with extracted fields arrives (the back is
  // stored, not read). idScanFill fills blanks only, so a repeat call is harmless.
  // ★★★ PROD-176 — A SCAN NEVER OVERWRITES A FIELD THAT HOLDS A VALUE, AND ON THIS SCREEN THAT
  //   IS LOAD-BEARING. This used to read `firstName: extracted.firstName || prev.firstName`:
  //   the scanned name REPLACED the account's. Under Alex's rule of 2026-09-23 (an ID whose
  //   name is not the account's is NOT ACCEPTED) that line was a bypass — a screen that
  //   renames the account to agree with whatever card is held up to it makes the mismatch
  //   unobservable here, and a refused patient who then saved would have had the stranger's
  //   name written onto their account. One rule now, shared with onboarding: fill blanks.
  const cardPair = useCardScanPair('ID', (extracted) => {
    setForm(prev => ({ ...prev, ...idScanFill(prev, extracted) }))
  })

  const handleCardCapture = (side, cardData, scanToken) => {
    // PROD-1189b: an upload took its token before the extract started; a result whose side was
    // cleared or replaced since then is dropped whole (no state write, no fill).
    if (scanToken && !cardPair.isCurrent(scanToken)) return
    // ★★ TEST 1.02 R2 — A REFUSED FRONT IS NOT KEPT, AND NEITHER IS A BACK WHILE IT STANDS:
    //   the server drops idFront AND idBack while the verdict on the row is 'mismatched', so
    //   drawing either as captured would promise something the save will undo. The camera
    //   path and the upload path both arrive here, so this one test covers both.
    if (side === 'idFront' && isIdScanRefused(cardData)) {
      setIdRefused(true)
      setIdAcceptedNow(false)
      cardPair.clearSide('idFront')
      cardPair.clearSide('idBack')
      setScannerOpen(null)
      return
    }
    if (side === 'idFront') { setIdRefused(false); setIdAcceptedNow(true) }
    if (side === 'idBack' && idRefused) { setScannerOpen(null); return }
    // Store the image immediately so the slot shows captured. PROD-1189: an ID front with
    // extracted fields auto-fills at once (via the pair hook); the back is only stored.
    const token = scanToken || cardPair.beginSide(side)
    setForm(prev => ({ ...prev, [side]: cardData }))
    cardPair.captureSide(side, cardData, token)
    setScannerOpen(null)
    // v242.37.0: upload the camera shot to storage so it gets a durable storagePath
    // (like onboarding). Without this, _slimCard drops the base64 on save and the
    // card can't be re-presigned on read → broken image. Merge when it lands.
    setCardUploads((n) => n + 1)
    persistCardData(cardData, 'ID', side === 'idFront' ? 'front' : 'back').then((enriched) => {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: removed or replaced meanwhile
      if (enriched && enriched.storagePath) {
        setForm(prev => ({ ...prev, [side]: enriched }))
        cardPair.captureSide(side, enriched, token)
      } else if (enriched && enriched.uploadError) {
        // ★ P2 Release 3: the photo was NOT stored and will not survive the save. Say so.
        alert(cardUploadFailureMessage(t, enriched.uploadError))
      }
    }).catch(() => {}).finally(() => setCardUploads((n) => Math.max(0, n - 1)))
  }

  const removeFile = (side) => { setForm(prev => ({ ...prev, [side]: null })); cardPair.clearSide(side) }

  // ★★★ TEST 1.02 — THE NAME THE SERVER READ OFF THIS ID, COMPARED TO THIS ACCOUNT.
  //   `identification.idNameCheck` is written by the API at the moment the FRONT of an ID
  //   is read (it is the only moment the document's name and the account exist in the same
  //   place) and it is SERVER-OWNED: this screen round-trips the whole section on save and
  //   the API strips the key back out, so what is drawn here can never be what this client
  //   sent. Three answers: 'matched', 'mismatched', 'not_compared'.
  // ★★ ONLY THE ONE THE PATIENT CAN ACT ON IS DRAWN. 'matched' needs no sentence and
  //   'not_compared' — we could not look — is not a thing to ask a patient about, and it
  //   does NOT refuse the card. Since Alex's decision of 2026-09-23 a mismatch REFUSES the
  //   card (the server will not keep it) — but it refuses THE CARD ONLY: lodging an ID stays
  //   optional, `canSave` above is untouched, and every other field on this form saves.
  const ID_NAME_MISMATCHED = 'mismatched'
  // ★★ R2 — a stored 'mismatched' means the LAST front scanned was REFUSED, and a refused card
  //   is by definition NOT on file — so this no longer waits for an idFront. A front accepted
  //   in this session supersedes it.
  const idNameMismatch = idRefused
    || (form.idNameCheck?.status === ID_NAME_MISMATCHED && !idAcceptedNow)

  const cardUrls = useResolvedCardUrls({ idFront: form.idFront, idBack: form.idBack })

  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
  ]

  // v242.61.0: same source as the headline and the save gate (IDENTIFICATION_REQUIRED_FIELDS).
  const canSave = isIdentificationComplete(form)

  // Shared input classes
  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
  const inputStyle = { color: '#15354F' }

  return (
    <>
      <ProfileSubpageLayout sectionId="identification" icon={CreditCard} onSave={handleSave} saving={saving || syncing || cardUploads > 0} busyLabel={cardUploads > 0 ? t('customDoc.uploading') : undefined} canSave={canSave}>
        {/* Photo ID Section */}
        <div className="rounded-2xl p-4 mb-5 border-[1.5px]" style={{ borderColor: 'rgba(70,168,249,0.2)', background: 'rgba(233,248,255,0.4)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(70,168,249,0.1)' }}>
              <CreditCard size={20} style={{ color: '#46A8F9' }} />
            </div>
            <div>
              <h3 className="text-[13px] font-bold" style={{ color: '#15354F' }}>{t('profileId.photoId')}</h3>
              <p className="text-[11px] text-gray-400">{t('profileIdentification.scanToAutofillYourInformation')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {['idFront', 'idBack'].map((side) => (
              <div key={side}>
                {form[side] ? (
                  <div className="relative aspect-[1.586/1] bg-white rounded-xl overflow-hidden border border-gray-200">
                    <CardThumb card={form[side]} resolved={cardUrls[side]} alt={side === 'idFront' ? 'ID Front' : 'ID Back'} />
                    <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Check size={10} />{side === 'idFront' ? 'Front' : 'Back'}
                    </div>
                    <button onClick={() => removeFile(side)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow"><X size={12} /></button>
                  </div>
                ) : (
                  <button onClick={() => setScannerOpen(side)} className="w-full aspect-[1.586/1] border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1.5 active:scale-[0.97] transition-all" style={{ background: 'rgba(255,255,255,0.7)' }}>
                    <Scan size={22} className="text-gray-400" />
                    <span className="text-[11px] font-medium text-gray-400">{side === 'idFront' ? t('profileId.scanFront') : t('profileId.scanBack')}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
          {idNameMismatch && (
            <div className="mt-3 rounded-xl p-3 border-[1.5px]" style={{ borderColor: 'rgba(214,158,46,0.4)', background: 'rgba(255,249,235,0.95)' }}>
              <p className="text-[12px] font-bold" style={{ color: '#7A5A10' }}>{t('profileId.idNameMismatchTitle')}</p>
              <p className="text-[11px] mt-1 leading-snug" style={{ color: '#7A5A10' }}>{t('profileId.idNameMismatchBody')}</p>
            </div>
          )}
        </div>

        {/* Personal Information */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-4 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileIdentification.personalInformation')}</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.firstName')} <span className="text-red-500">*</span></label>
            <input type="text" value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.lastName')} <span className="text-red-500">*</span></label>
            <input type="text" value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} className={inputCls} style={inputStyle} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.dob')} <span className="text-red-500">*</span></label>
            <DobPicker value={form.dob} onChange={(v) => setForm({...form, dob: v})} minAge={18} />
          </div>
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.sex')} <span className="text-red-500">*</span></label>
            <select value={form.sex} onChange={(e) => setForm({...form, sex: e.target.value})} className={inputCls + ' bg-white appearance-none'} style={{ ...inputStyle, height: 42, minHeight: 42, maxHeight: 42 }}>
              <option value="">{t('profileId.selectSex') || 'Select...'}</option>
              {/* TEST 3.06: lowercase VALUES, one spelling with every writer; labels unchanged. */}
              <option value="male">{t('profileId.male')}</option>
              <option value="female">{t('profileId.female')}</option>
              <option value="other">{t('profileId.other')}</option>
              <option value="prefer not to say">{t('profileIdentification.preferNotToSay')}</option>
            </select>
          </div>
        </div>

        {/* Address */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-4 pb-1 inline-block mt-2" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileIdentification.address')}</p>

        <div className="mb-4">
          <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.streetAddress')} <span className="text-red-500">*</span></label>
          <input type="text" value={form.streetAddress} onChange={(e) => setForm({...form, streetAddress: e.target.value})} className={inputCls} style={inputStyle} />
        </div>
        <div className="mb-4">
          <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.apt')}</label>
          <input type="text" value={form.apt} onChange={(e) => setForm({...form, apt: e.target.value})} className={inputCls} style={inputStyle} />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.city')} <span className="text-red-500">*</span></label>
            <input type="text" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.state')} <span className="text-red-500">*</span></label>
            <select value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} className={inputCls + ' bg-white'} style={inputStyle}>
              <option value="">...</option>
              {US_STATES.map(state => <option key={state} value={state}>{state}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mb-4">
          <div className="w-1/3">
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.zipCode')} <span className="text-red-500">*</span></label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              pattern="[0-9]*"
              value={form.zipCode}
              onChange={(e) => {
                // v213.3 (C-A-194): strip non-digit chars and clamp to 5 digits.
                // Prevents negative numbers, letters, hyphens, etc.
                const digits = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 5)
                setForm({ ...form, zipCode: digits })
              }}
              className={inputCls}
              style={inputStyle}
            />
          </div>
          <div className="flex-1">
            <label className="block text-[12px] font-semibold text-gray-700 mb-1">{t('profileIdentification.mobileNumber')}</label>
            <input type="tel" value={user?.phone || ''} readOnly disabled aria-readonly="true" data-testid="identification-phone" className={inputCls + ' bg-gray-50 text-gray-500'} style={inputStyle} />
          </div>
        </div>
        {/* PROD-1167 (Alex 2026-10-27): the number is read-only here. The "Change" link (the
            code-verified flow, OnboardingPhone) comes only together with the PROD-1154 switch-on
            in PROD, and the client has no signal for that switch yet (the app learns of 1154
            only from a 403 2fa_current_factor_required), so for now the support line is the way. */}
        <p className="text-[11px] text-gray-500 -mt-2 mb-1" data-testid="identification-phone-note">{t('profileIdentification.phoneNote')}</p>
        <p className="text-[11px] text-gray-500 mb-2" data-testid="identification-phone-support">{t('profileIdentification.phoneSupport')}</p>
      </ProfileSubpageLayout>

      {scannerOpen && (
        <CardScanner cardType="ID" side={scannerOpen === 'idFront' ? 'front' : 'back'}
          onCapture={(cardData) => handleCardCapture(scannerOpen, cardData)} onCancel={() => setScannerOpen(null)}
          onUpload={async (file) => { const side = scannerOpen; const scanToken = cardPair.beginSide(side); try { const cardData = await processCardFile(file, 'ID', side === 'idFront' ? 'front' : 'back'); handleCardCapture(side, cardData, scanToken) } catch (e) { if (!cardPair.isCurrent(scanToken)) return; alert(e?.message || 'Could not read that image — try another photo.') } }}
          initialImage={cardUrls[scannerOpen] || cardInlineSrc(form[scannerOpen])} enableOCR={scannerOpen === 'idFront'} />
      )}
    </>
  )
}
